import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

/* =====================================================
   MODAL
===================================================== */
function DriverModal({ open, onClose, onSave, driver, season }) {
  const [form, setForm] = useState({
    position: "",
    name: "",
    team: "",
    points: 0,
    movement: "same",
  });

  useEffect(() => {
    if (!open) return;

    if (driver) {
      setForm({
        position: String(driver.position),
        name: driver.name,
        team: driver.team,
        points: driver.points,
        movement: driver.movement,
      });
    } else {
      setForm({
        position: "",
        name: "",
        team: "",
        points: 0,
        movement: "same",
      });
    }
  }, [open, driver]);

  if (!open) return null;

  const validate = () => {
    if (!form.position || Number(form.position) <= 0)
      return "Position is required";
    if (!form.name.trim()) return "Driver name is required";
    if (!season) return "Season not selected";
    return null;
  };

  const submit = () => {
    const error = validate();
    if (error) {
      alert(error);
      return;
    }

    onSave({
      season,
      position: Number(form.position),
      name: form.name.trim(),
      team: form.team.trim(),
      points: Number(form.points),
      movement: form.movement,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-neutral-900 w-full max-w-md rounded-xl border border-neutral-800 p-6">
        <h3 className="text-lg font-semibold mb-4">
          {driver ? "Edit Driver" : "Add Driver"}
        </h3>

        <div className="space-y-3">
          <input
            type="number"
            placeholder="Position"
            value={form.position}
            onChange={(e) =>
              setForm({ ...form, position: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            placeholder="Driver Name"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            placeholder="Team"
            value={form.team}
            onChange={(e) =>
              setForm({ ...form, team: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            type="number"
            placeholder="Points"
            value={form.points}
            onChange={(e) =>
              setForm({ ...form, points: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <select
            value={form.movement}
            onChange={(e) =>
              setForm({ ...form, movement: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          >
            <option value="up">↑ Up</option>
            <option value="down">↓ Down</option>
            <option value="same">– Same</option>
          </select>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="text-neutral-400">
            Cancel
          </button>
          <button
            onClick={submit}
            className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded-lg text-white font-semibold"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   MAIN
===================================================== */
export default function DriversStandings({ admin }) {
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");
  const [drivers, setDrivers] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);

  /* LOAD SEASONS */
  useEffect(() => {
    return onSnapshot(collection(db, "standings_drivers"), (snap) => {
      const yrs = [
        ...new Set(snap.docs.map((d) => d.data().season)),
      ].sort((a, b) => b - a);

      setSeasons(yrs);
      if (!activeSeason && yrs.length) setActiveSeason(yrs[0]);
    });
  }, [activeSeason]);

  /* LOAD DRIVERS */
  useEffect(() => {
    if (!activeSeason) {
      setDrivers([]);
      return;
    }

    return onSnapshot(
      query(
        collection(db, "standings_drivers"),
        where("season", "==", activeSeason)
      ),
      (snap) => {
        setDrivers(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => a.position - b.position)
        );
      }
    );
  }, [activeSeason]);

  /* SAVE */
  const saveDriver = async (data) => {
    if (editingDriver) {
      await updateDoc(
        doc(db, "standings_drivers", editingDriver.id),
        {
          ...data,
          updatedAt: serverTimestamp(),
          updatedBy: admin.email,
        }
      );
    } else {
      await addDoc(collection(db, "standings_drivers"), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: admin.email,
      });
    }

    setModalOpen(false);
    setEditingDriver(null);
  };

  return (
    <div className="bg-neutral-900 p-6 rounded-xl border border-neutral-800">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Drivers Standings</h2>

        <div className="flex gap-3 items-center">
          <select
            value={activeSeason}
            onChange={(e) => setActiveSeason(e.target.value)}
            className="h-10 w-32 bg-neutral-950 border border-neutral-800 px-4 rounded-lg text-sm"
          >
            <option value="">Select season</option>
            {seasons.map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>

          <button
            disabled={!activeSeason}
            onClick={() => setModalOpen(true)}
            className={`h-10 px-4 rounded-lg font-semibold transition ${
              activeSeason
                ? "bg-red-600 hover:bg-red-500 text-white"
                : "bg-neutral-700 text-neutral-400 cursor-not-allowed"
            }`}
          >
            + Add Driver
          </button>
        </div>
      </div>

      {/* TABLE */}
      {drivers.length === 0 ? (
        <div className="text-sm text-neutral-500 text-center py-6">
          {seasons.length === 0
            ? "No drivers yet. Add the first driver to create a season."
            : "No drivers for this season."}
        </div>
      ) : (
        <div className="space-y-2">
          {drivers.map((d) => (
            <div
              key={d.id}
              className="grid grid-cols-6 gap-2 bg-neutral-950 p-3 rounded-lg items-center"
            >
              <div>{d.position}</div>
              <div>{d.name}</div>
              <div>{d.team}</div>
              <div>{d.points} pts</div>
              <div>{d.movement}</div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setEditingDriver(d);
                    setModalOpen(true);
                  }}
                  className="text-neutral-400 hover:text-white"
                >
                  Edit
                </button>
                <button
                  onClick={() =>
                    deleteDoc(
                      doc(db, "standings_drivers", d.id)
                    )
                  }
                  className="text-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DriverModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingDriver(null);
        }}
        onSave={saveDriver}
        driver={editingDriver}
        season={activeSeason}
      />
    </div>
  );
}
